import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

let app: FirebaseApp | null = null;
let emulatorsConnected = false;
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);

const env = import.meta.env as Record<string, string | undefined>;

const requiredEnv = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

const isEmulatorMode = (): boolean => env.VITE_USE_FIREBASE_EMULATORS === 'true';
const hasRequiredEnv = (): boolean => requiredEnv.every((key) => Boolean(env[key]));
const parsePort = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const resolveLoopbackEmulatorHost = (configuredHost: string): string => {
  if (!LOOPBACK_HOSTS.has(configuredHost)) {
    return configuredHost;
  }

  if (typeof window === 'undefined') {
    return configuredHost;
  }

  const pageHost = window.location.hostname.trim();
  return LOOPBACK_HOSTS.has(pageHost) ? pageHost : configuredHost;
};

const warnIfProjectIdLooksInvalid = (projectId: string): void => {
  if (/^\d+$/.test(projectId)) {
    console.warn(
      'VITE_FIREBASE_PROJECT_ID appears to be a numeric project number. Use the string Firebase project ID instead.',
    );
  }
};

const maybeConnectEmulators = (): void => {
  if (emulatorsConnected || env.VITE_USE_FIREBASE_EMULATORS !== 'true') {
    return;
  }

  const configuredHost = env.VITE_FIREBASE_EMULATOR_HOST ?? 'localhost';
  const host = resolveLoopbackEmulatorHost(configuredHost);
  const authPort = parsePort(env.VITE_FIREBASE_AUTH_EMULATOR_PORT, 9099);
  const firestorePort = parsePort(env.VITE_FIRESTORE_EMULATOR_PORT, 8081);
  const functionsPort = parsePort(env.VITE_FUNCTIONS_EMULATOR_PORT, 5001);
  const storagePort = parsePort(env.VITE_FIREBASE_STORAGE_EMULATOR_PORT, 9199);

  connectAuthEmulator(getAuth(initializeFirebase()), `http://${host}:${authPort}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(getFirestore(initializeFirebase()), host, firestorePort);
  connectFunctionsEmulator(getFunctions(initializeFirebase()), host, functionsPort);
  connectStorageEmulator(getStorage(initializeFirebase()), host, storagePort);

  emulatorsConnected = true;
};

export const initializeFirebase = (): FirebaseApp => {
  if (app) return app;

  if (getApps().length > 0) {
    app = getApp();
    return app;
  }

  if (!hasRequiredEnv() && !isEmulatorMode()) {
    throw new Error('Firebase environment variables are not fully configured.');
  }

  const emulatorProjectId = env.VITE_FIREBASE_PROJECT_ID ?? 'demo-waysprite';
  const projectId = env.VITE_FIREBASE_PROJECT_ID ?? emulatorProjectId;
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN ?? `${projectId}.firebaseapp.com`;
  warnIfProjectIdLooksInvalid(projectId);

  app = initializeApp({
    apiKey: env.VITE_FIREBASE_API_KEY ?? 'demo-api-key',
    authDomain,
    projectId,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID ?? '1:1234567890:web:demo',
  });

  return app;
};

export const getFirebaseAuth = () => {
  maybeConnectEmulators();
  return getAuth(initializeFirebase());
};

export const getFirebaseDb = () => {
  maybeConnectEmulators();
  return getFirestore(initializeFirebase());
};

export const getFirebaseFunctions = () => {
  maybeConnectEmulators();
  return getFunctions(initializeFirebase());
};

export const getFirebaseStorage = () => {
  maybeConnectEmulators();
  return getStorage(initializeFirebase());
};
